import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Eye, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

interface QuickStatusActionsProps {
  report: any;
  onStatusUpdate: (reportId: string, status: string) => void;
  isUpdating: boolean;
}

export const QuickStatusActions = ({ report, onStatusUpdate, isUpdating }: QuickStatusActionsProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'reviewed': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-300';
      case 'dismissed': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getNextActions = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return [
          { status: 'reviewed', label: 'Start Review', icon: Eye, color: 'blue' },
          { status: 'dismissed', label: 'Dismiss', icon: XCircle, color: 'gray' }
        ];
      case 'reviewed':
        return [
          { status: 'resolved', label: 'Resolve', icon: CheckCircle, color: 'green' },
          { status: 'dismissed', label: 'Dismiss', icon: XCircle, color: 'gray' }
        ];
      case 'resolved':
        return [
          { status: 'reviewed', label: 'Reopen Review', icon: Eye, color: 'blue' }
        ];
      case 'dismissed':
        return [
          { status: 'reviewed', label: 'Reopen Review', icon: Eye, color: 'blue' }
        ];
      default:
        return [];
    }
  };

  const nextActions = getNextActions(report.status);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Report Status Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Current Status:</span>
          <Badge className={`${getStatusColor(report.status)} capitalize`}>
            {report.status === 'reviewed' ? 'Under Review' : report.status}
          </Badge>
        </div>

        {nextActions.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm font-medium">Quick Actions:</span>
            <div className="flex flex-wrap gap-2">
              {nextActions.map((action) => {
                const IconComponent = action.icon;
                return (
                  <Button
                    key={action.status}
                    size="sm"
                    variant={action.color === 'green' ? 'default' : 'outline'}
                    onClick={() => onStatusUpdate(report.id, action.status)}
                    disabled={isUpdating}
                    className={
                      action.color === 'green' 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : action.color === 'blue'
                        ? 'border-blue-300 text-blue-700 hover:bg-blue-50'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }
                  >
                    <IconComponent className="h-4 w-4 mr-1" />
                    {action.label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-muted/50 p-3 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium">Status Workflow:</p>
              <ul className="mt-1 space-y-1 text-xs">
                <li>• <strong>Pending:</strong> New report awaiting review</li>
                <li>• <strong>Under Review:</strong> Investigation in progress</li>
                <li>• <strong>Resolved:</strong> Action taken, case closed</li>
                <li>• <strong>Dismissed:</strong> No action needed</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};